import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/info-icon.svg';
import {
  Button,
  CodeEditor,
  Flex,
  FlexColumn,
  FormControls,
} from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { styled, Tooltip, Typography } from '@mui/material';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';
import {
  QueryTestConfig,
  YamlTestConfig,
  SourceFreshnessTestConfig,
} from '../../types';
import TestMetadataFields from './TestMetadataFields';

const InfoIconStyled = styled(InfoIcon)`
  min-width: 16px;
  margin-top: ${space('hairline')};
`;

type Props = {
  testConfig: QueryTestConfig | YamlTestConfig | SourceFreshnessTestConfig;
  onEdit?: () => void;
};

const CodeTestConfiguration = ({ testConfig, onEdit }: Props) => {
  const hasPermission = useEnvPermission();
  const selectedEnvId = useSelectedEnv();
  const warehouseType = useSelectedWarehouseType();
  const { isRepositoryConnected } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });

  const hasEditPermission = hasPermission('EDIT_TEST_CONFIGURATIONS');
  const isSourceFreshness = testConfig.type === 'source_freshness';
  const isEditDisabled = !hasEditPermission || !isRepositoryConnected;

  const editDisabledTooltip = !hasEditPermission
    ? 'No permissions for editing tests'
    : !isRepositoryConnected
    ? 'Connect your code repository to enable test management capabilities'
    : undefined;

  const infoText = isEditDisabled
    ? 'This test can only be edited in code.'
    : `Click "Edit" to suggest changes and submit a pull request.`;

  return (
    <FormControls.TestAccordion summary="Test Settings" defaultExpanded>
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <Flex sx={{ justifyContent: 'space-between' }}>
          <FlexColumn sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: fontWeight('medium') }}>
              Test Parameters
            </Typography>
            {!!onEdit && (
              <Flex sx={{ gap: 1 }}>
                <InfoIconStyled />
                <Typography
                  sx={{ fontSize: fontSize('sm'), color: 'content.secondary' }}
                >
                  {infoText}
                </Typography>
              </Flex>
            )}
          </FlexColumn>

          {!!onEdit && (
            <Tooltip title={editDisabledTooltip}>
              <Flex sx={{ height: 'fit-content' }}>
                <Button onClick={onEdit} disabled={isEditDisabled}>
                  Edit
                </Button>
              </Flex>
            </Tooltip>
          )}
        </Flex>

        <CodeEditor
          maxLines={20}
          mode={testConfig.type === 'query' ? 'sql' : 'yaml'}
          warehouseType={warehouseType}
          value={testConfig.config}
          readOnly
        />

        {!isSourceFreshness && (
          <TestMetadataFields
            description={testConfig.description}
            tags={testConfig.tags}
            owners={testConfig.owners}
            severity={testConfig.severity}
            errorIf={testConfig.errorIf}
            warnIf={testConfig.warnIf}
          />
        )}
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default CodeTestConfiguration;
